import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page, PageHeader } from '@tanium/react-page';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import { TooltippedButton } from '@tanium/react-button';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { Notification } from '@tanium/react-persistent-notifications';
import { Shimmer } from '@tanium/react-shimmer';
import { ComputerSupportIcon, EditIcon, TrashIcon } from '@tanium/react-graphics';
import { Link } from '@tanium/react-link';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { RibbonColumn, StackedRibbonListStatic } from '@tanium/react-stacked-ribbon-list';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { AsyncState, AsyncStatus } from '../../hooks/useAsync';
import { useCaseGrants } from '../../hooks/useCaseGrants';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { useModalState } from '../../hooks/useModalState';
import { useTenant } from '../../hooks/useTenant';
import { Permission } from '../../models/Permissions';
import { CaseGrant, CaseRole, SFAccountCaseGrants } from '../../models/CaseManagementModels';
import { formatDate } from '../../utilities/dateHelper';
import { alwaysTrueOp } from '../../utilities/commonFuncs';
import { CaseGrantModal } from './CaseGrantModal';

export type CaseRoleOption = { label: string; value: CaseRole };

export const useCaseRoleOptions = (): CaseRoleOption[] => {
  const { t } = useTranslation();
  return [
    { label: t('Case Admin'), value: CaseRole.CaseAdmin },
    { label: t('Case User'), value: CaseRole.CaseUser },
  ];
};

const SFAccountSection: React.VFC<{
  account: SFAccountCaseGrants;
  canManage: boolean;
  upsertCaseGrant: AsyncState<void, (crmAccountId: string, email: string, role: CaseRole) => Promise<void>>;
  onRemove: (crmAccountId: string, email: string) => Promise<void>;
}> = ({ account, canManage, upsertCaseGrant, onRemove }) => {
  const { t } = useTranslation();
  const roleOptions = useCaseRoleOptions();
  const caseGrantModal = useModalState<CaseGrant>();
  const removeConfirmModal = useModalState<CaseGrant>();
  const [isRemoving, setIsRemoving] = useState(false);

  const columns: RibbonColumn<CaseGrant>[] = [
    {
      label: t('Email address'),
      dataKey: 'email',
      isSortDisabled: true,
    },
    {
      label: t('Role'),
      dataKey: 'role',
      width: 160,
      isSortDisabled: true,
      renderer: ({ itemData }) => roleOptions.find((option) => option.value === itemData.role)?.label ?? itemData.role,
    },
    {
      label: t('Last Updated'),
      dataKey: 'updatedAt',
      width: 220,
      isSortDisabled: true,
      renderer: ({ itemData }) => formatDate(new Date(itemData.updatedAt)),
    },
    {
      label: t('Actions'),
      dataKey: '',
      alignment: 'center',
      width: 80,
      isSortDisabled: true,
      renderer: ({ itemData }) => (
        <Stack itemSpacing={2}>
          <TooltippedIconButton
            variant="primary"
            label={`${t('Edit')} ${itemData.email}`}
            icon={EditIcon}
            width={16}
            height={16}
            disabled={!canManage}
            tooltipContent={canManage ? t('Edit') : t('You have insufficient permissions to manage case access.')}
            onClick={() => caseGrantModal.openModal(itemData)}
          />
          <TooltippedIconButton
            variant="primary"
            label={`${t('Remove')} ${itemData.email}`}
            icon={TrashIcon}
            width={16}
            height={16}
            disabled={!canManage}
            tooltipContent={canManage ? t('Remove') : t('You have insufficient permissions to manage case access.')}
            onClick={() => removeConfirmModal.openModal(itemData)}
          />
        </Stack>
      ),
    },
  ];

  return (
    <PageSection
      title={account.crmAccountId}
      additionalTitleContent={
        canManage && (
          <Stack width="100%" direction="row" justifyContent="right" alignItems="center">
            <TooltippedButton size="sm" onClick={() => caseGrantModal.openModal()}>
              {t('Add Email')}
            </TooltippedButton>
          </Stack>
        )
      }
    >
      <Stack direction="column" itemSpacing={2}>
        {account.sharedWithOtherTenants && (
          <Notification role="note" type="info">
            {t('Other AMP tenants also manage the Case Admin and Case User access for this CRM Account.')}
          </Notification>
        )}
        {account.grants.length === 0 ? (
          <Notification role="note" type="info">
            {t('No emails have Support Case Management access for this CRM Account yet.')}
          </Notification>
        ) : (
          <StackedRibbonListStatic<string, CaseGrant>
            columns={columns}
            dataGetter={(i) => account.grants[i]}
            isItemLoaded={alwaysTrueOp}
            itemCount={account.grants.length}
            totalItemCount={account.grants.length}
            itemIdGetter={(i) => account.grants[i].email}
          />
        )}
      </Stack>
      <CaseGrantModal
        crmAccountId={account.crmAccountId}
        grant={caseGrantModal.data}
        isOpen={caseGrantModal.isModalOpen}
        onRequestClose={caseGrantModal.closeModal}
        upsertCaseGrant={upsertCaseGrant}
      />
      <ConfirmModal
        isOpen={removeConfirmModal.isModalOpen}
        header={t('Confirm Remove Case Access')}
        isConfirmDisabled={isRemoving}
        onConfirm={async () => {
          setIsRemoving(true);
          try {
            // removeConfirmModal.data is always set here: onConfirm is only reachable
            // through the trash icon's openModal(itemData) call below.
            await onRemove(account.crmAccountId, removeConfirmModal.data!.email);
          } finally {
            setIsRemoving(false);
            removeConfirmModal.closeModal();
          }
        }}
        onCancel={removeConfirmModal.closeModal}
      >
        {/* ConfirmModal's children render on every SFAccountSection render regardless of
            isOpen, so removeConfirmModal.data (unlike inside onConfirm above) may be
            undefined here before the trash icon has ever been clicked. */}
        <>{t('Are you sure you wish to remove case access for email?', { email: removeConfirmModal.data?.email })}</>
      </ConfirmModal>
    </PageSection>
  );
};

// CaseGrantsManager owns useCaseGrants (and thus the GET /v1/case-grants fetch it fires
// on mount). It is only ever mounted by CaseManagementSection once the viewer is
// known to have GetCaseGrants and the tenant is known to have caseManagementEnabled --
// never render this unconditionally, or the fetch fires before that guard can run.
const CaseGrantsManager: React.VFC<{ canManage: boolean }> = ({ canManage }) => {
  const { t } = useTranslation();
  const { accounts, loadCaseGrants, upsertCaseGrant, revokeCaseGrant } = useCaseGrants();
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (upsertCaseGrant.status === AsyncStatus.Success) {
      upsertCaseGrant.reset();
      showNotification({ type: 'success', contents: t('Successfully saved Support Case Management access') });
    } else if (upsertCaseGrant.status === AsyncStatus.Failure) {
      upsertCaseGrant.reset();
      showNotification({ type: 'error', contents: t('Failed to save Support Case Management access') });
    }
  }, [t, upsertCaseGrant, showNotification]);

  useEffect(() => {
    if (revokeCaseGrant.status === AsyncStatus.Success) {
      revokeCaseGrant.reset();
      showNotification({ type: 'success', contents: t('Successfully removed Support Case Management access') });
    } else if (revokeCaseGrant.status === AsyncStatus.Failure) {
      revokeCaseGrant.reset();
      showNotification({ type: 'error', contents: t('Failed to remove Support Case Management access') });
    }
  }, [t, revokeCaseGrant, showNotification]);

  return (
    <AsyncSuspense
      state={loadCaseGrants}
      loading={
        <Well mt={1} p={1}>
          <Box mt={2} height={1} width="100%">
            <Shimmer />
          </Box>
        </Well>
      }
      failure={
        <Well mt={1} p={1}>
          <Notification role="alert" type="error">
            {t('Failed to load Support Case Management access')}
          </Notification>
        </Well>
      }
    >
      <>
        {accounts.length > 1 && (
          <Notification role="note" type="info">
            {t(
              'Your Tanium Account is associated with multiple CRM Accounts. You may configure access to them individually below.'
            )}
          </Notification>
        )}
        {accounts.length === 0 ? (
          <Well mt={1} p={1}>
            <Notification role="note" type="info">
              {t('No CRM Accounts are related to this Tanium Account yet.')}
            </Notification>
          </Well>
        ) : (
          accounts.map((account) => (
            <Well key={account.crmAccountId} mt={1} p={1}>
              <SFAccountSection
                account={account}
                canManage={canManage}
                upsertCaseGrant={upsertCaseGrant}
                onRemove={(crmAccountId, email) => revokeCaseGrant.execute(crmAccountId, email)}
              />
            </Well>
          ))
        )}
      </>
    </AsyncSuspense>
  );
};

export const CaseManagementSection: React.VFC = () => {
  const { t } = useTranslation();
  const { tenant, loadTenant } = useTenant();
  const canView = useHasPermissions([Permission.GetCaseGrants]);
  const canManage = useHasPermissions([Permission.ManageCaseGrants]);

  const pageHeader = (
    <PageHeader
      icon={ComputerSupportIcon}
      heading={t('Support Case Management')}
      description={
        <Link target="_blank" href="https://community.tanium.com">
          {t('Tanium Support Portal')}
        </Link>
      }
    />
  );

  // The tenant request hasn't settled yet: render the same loading UI as the grants
  // load rather than falling into the "not available" branch below, which would flash
  // on direct navigation/refresh before caseManagementEnabled is known.
  if (loadTenant.status === AsyncStatus.Idle || loadTenant.status === AsyncStatus.Pending) {
    return (
      <Page header={pageHeader}>
        <Well p={1}>
          <Box mt={2} height={1} width="100%">
            <Shimmer />
          </Box>
        </Well>
      </Page>
    );
  }

  if (!tenant?.caseManagementEnabled || !canView) {
    return (
      <Page header={pageHeader}>
        <Well p={1}>
          <Notification role="alert" type="error">
            {t('Support Case Management is not available for this Tanium Account.')}
          </Notification>
        </Well>
      </Page>
    );
  }

  return (
    <Page header={pageHeader}>
      <CaseGrantsManager canManage={canManage} />
    </Page>
  );
};

export default CaseManagementSection;
